<script setup>
// NOTICE: KAPTION-INTEGRATION
// reply-template-picker.EDITOR.1..3 — the picked template inside the reply box:
// the fixed text with one inline field per variable (prefilled with the
// wizard's smart defaults), plus the media header upload and dynamic button
// parameters when the template has them. Modelled on
// KaptionInlineUtilityTemplateEditor's look; emits the values and validity.
// reply-template-picker.TYPES.1..4 — every sendable shape is filled in place:
// a TEXT header variable inline in the header, the media header as a tile that
// starts with the template's own file, a dynamic link as "prefix + [suffix]",
// a coupon code capped at Meta's 15 characters, and the fixed buttons shown
// read-only so the box previews the whole message.
import { reactive, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  buildInitialBodyValues,
  buildInitialHeaderMedia,
  buildInitialHeaderValues,
  COPY_CODE_MAX_LENGTH,
  getTemplateFooter,
  hasMediaHeader,
  isComposeValid,
  normalizeUrlButtonParam,
  parseTemplateButtons,
} from 'dashboard/components-next/NewConversationModal/helpers/newConversationHelpers';
import KaptionTemplateHeaderMedia from './KaptionTemplateHeaderMedia.vue';
import {
  sanitizeTemplateValue,
  splitTemplateBody,
} from './kaptionReplyTemplateHelpers';

const props = defineProps({
  template: { type: Object, required: true },
  contact: { type: Object, default: null },
  disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['change']);

const { t } = useI18n();

const component = type =>
  props.template?.components?.find(c => c.type === type) || null;
const bodyText = computed(() => component('BODY')?.text || '');
const bodyParts = computed(() => splitTemplateBody(bodyText.value));
const headerParts = computed(() => {
  const header = component('HEADER');
  return header?.format === 'TEXT' ? splitTemplateBody(header.text) : [];
});
const footerText = computed(() => getTemplateFooter(props.template));
const isMediaTemplate = computed(() => hasMediaHeader(props.template));
const buttons = computed(() => parseTemplateButtons(props.template));

const bodyValues = reactive(
  buildInitialBodyValues(props.template, { contact: props.contact })
);
const headerValues = reactive(buildInitialHeaderValues(props.template));
const headerMedia = reactive(buildInitialHeaderMedia(props.template));
const buttonValues = reactive({});

const isValid = computed(() =>
  isComposeValid(
    props.template,
    bodyValues,
    headerMedia,
    buttonValues,
    headerValues
  )
);

const emitChange = () => {
  emit('change', {
    bodyValues: { ...bodyValues },
    headerValues: { ...headerValues },
    headerMedia: { ...headerMedia },
    buttonValues: { ...buttonValues },
    isValid: isValid.value,
  });
};

watch([bodyValues, headerValues, headerMedia, buttonValues], emitChange, {
  deep: true,
});
onMounted(emitChange);

const setBodyValue = (key, value) => {
  bodyValues[key] = sanitizeTemplateValue(value);
};
const setHeaderValue = (key, value) => {
  headerValues[key] = sanitizeTemplateValue(value);
};
const setHeaderMedia = value => Object.assign(headerMedia, value);
// reply-template-picker.TYPES.3 — a pasted full link collapses to its suffix.
const normalizeUrlValue = btn => {
  buttonValues[btn.index] = normalizeUrlButtonParam(
    btn,
    buttonValues[btn.index]
  );
};

const fieldSize = (values, key, token) =>
  Math.max(String(values[key] || '').length, token.length, 4);

const buttonIcon = btn =>
  ({
    URL: 'i-lucide-external-link',
    PHONE_NUMBER: 'i-lucide-phone',
    COPY_CODE: 'i-lucide-copy',
    QUICK_REPLY: 'i-lucide-reply',
  })[btn.type] || 'i-lucide-square';
</script>

<template>
  <div
    class="flex flex-col gap-3 px-4 py-3"
    data-testid="picked-template-editor"
  >
    <p
      v-if="headerParts.length"
      class="text-sm font-semibold text-n-slate-12"
      data-testid="picked-template-header"
    >
      <template v-for="(part, index) in headerParts" :key="`h-${index}`">
        <span v-if="part.type === 'text'">{{ part.text }}</span>
        <input
          v-else
          :value="headerValues[part.key]"
          type="text"
          maxlength="60"
          :size="fieldSize(headerValues, part.key, part.token)"
          :placeholder="part.token"
          :disabled="disabled"
          :aria-label="
            t('KAPTION_REPLY_TEMPLATE.EDITOR.HEADER_VARIABLE', {
              name: part.key,
            })
          "
          :data-testid="`picked-header-var-${part.key}`"
          class="inline-variable reset-base"
          :class="headerValues[part.key]?.trim() ? 'is-filled' : 'is-empty'"
          @input="setHeaderValue(part.key, $event.target.value)"
        />
      </template>
    </p>

    <KaptionTemplateHeaderMedia
      v-if="isMediaTemplate"
      :template="template"
      :model-value="headerMedia"
      :disabled="disabled"
      @update:model-value="setHeaderMedia"
    />

    <!-- The editor IS the preview: fixed text, variables as inline fields. -->
    <div
      class="template-body whitespace-pre-wrap break-words text-n-slate-12"
      data-testid="picked-template-body"
    >
      <template v-for="(part, index) in bodyParts" :key="index">
        <span v-if="part.type === 'text'">{{ part.text }}</span>
        <input
          v-else
          :value="bodyValues[part.key]"
          type="text"
          :size="fieldSize(bodyValues, part.key, part.token)"
          :placeholder="part.token"
          :disabled="disabled"
          :aria-label="
            t('KAPTION_REPLY_TEMPLATE.EDITOR.VARIABLE', { name: part.key })
          "
          :data-testid="`picked-var-${part.key}`"
          class="inline-variable reset-base"
          :class="bodyValues[part.key]?.trim() ? 'is-filled' : 'is-empty'"
          @input="setBodyValue(part.key, $event.target.value)"
        />
      </template>
    </div>

    <p v-if="footerText" class="text-xs text-n-slate-10">
      {{ footerText }}
    </p>

    <div
      v-if="buttons.length"
      class="flex flex-wrap gap-2"
      data-testid="picked-template-buttons"
    >
      <div
        v-for="btn in buttons"
        :key="`btn-${btn.index}`"
        class="flex items-center max-w-full gap-1.5 px-2.5 py-1 text-xs border rounded-lg border-n-weak bg-n-alpha-black1 text-n-slate-11"
        :data-testid="`picked-btn-chip-${btn.index}`"
      >
        <span :class="buttonIcon(btn)" class="size-3.5 shrink-0" />
        <span class="font-medium text-n-slate-12 shrink-0">{{ btn.text }}</span>
        <template v-if="btn.paramKind === 'url'">
          <span
            class="truncate text-n-slate-10 max-w-[14rem]"
            :title="btn.urlPrefix"
          >
            {{ btn.urlPrefix }}
          </span>
          <input
            v-model="buttonValues[btn.index]"
            type="text"
            :placeholder="
              btn.example ? btn.example.replace(btn.urlPrefix, '') : '…'
            "
            :disabled="disabled"
            :aria-label="
              t('KAPTION_REPLY_TEMPLATE.EDITOR.BUTTON_URL', { name: btn.text })
            "
            :data-testid="`picked-btn-${btn.index}`"
            class="inline-variable reset-base min-w-[6ch]"
            :class="
              String(buttonValues[btn.index] || '').trim()
                ? 'is-filled'
                : 'is-empty'
            "
            @change="normalizeUrlValue(btn)"
          />
        </template>
        <input
          v-else-if="btn.paramKind === 'copy_code'"
          v-model="buttonValues[btn.index]"
          type="text"
          :maxlength="COPY_CODE_MAX_LENGTH"
          :placeholder="t('KAPTION_REPLY_TEMPLATE.EDITOR.CODE_PLACEHOLDER')"
          :disabled="disabled"
          :aria-label="
            t('KAPTION_REPLY_TEMPLATE.EDITOR.BUTTON_CODE', { name: btn.text })
          "
          :data-testid="`picked-btn-${btn.index}`"
          class="inline-variable reset-base min-w-[8ch]"
          :class="
            String(buttonValues[btn.index] || '').trim()
              ? 'is-filled'
              : 'is-empty'
          "
        />
        <span
          v-else-if="btn.type === 'URL' || btn.type === 'PHONE_NUMBER'"
          class="truncate text-n-slate-10 max-w-[14rem]"
        >
          {{ btn.url || btn.phone_number }}
        </span>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Same typography as KaptionInlineUtilityTemplateEditor. */
.template-body {
  font-size: 1.0625rem;
  line-height: 1.75;
}

.inline-variable {
  @apply inline outline-none border-none bg-transparent p-0 m-0 rounded-none;
  font: inherit;
  line-height: inherit;
  min-width: 3ch;
  max-width: 100%;
  height: auto;
  /* Grow with the value; `size` is the fallback where unsupported. */
  field-sizing: content;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 4px;
  text-decoration-thickness: 2px;
  transition: all 0.15s ease;

  &.is-empty {
    text-decoration-color: rgb(var(--ruby-6));

    &::placeholder {
      @apply text-n-ruby-9 italic;
    }

    &:hover {
      background: rgb(var(--ruby-3) / 0.3);
    }
  }

  &.is-filled {
    text-decoration-style: solid;
    text-decoration-color: rgb(var(--green-7));
    @apply text-n-green-11 font-medium;

    &:hover {
      background: rgb(var(--green-3) / 0.4);
    }
  }

  &:focus {
    background: rgb(var(--green-3) / 0.5);
    text-decoration-color: rgb(var(--green-9));
    text-decoration-style: solid;
  }
}
</style>
